import { useState, useEffect, useCallback, useMemo } from 'react';
import type { PaymentSystem } from '../types/payment';
import { matchesSearchText } from '../utils';
import { requestEdgeFunction } from '../services/edgeFunctionClient';

export interface PaymentRow {
  paymentId: number;
  studentId: number;
  studentName: string;
  paymentSystem: PaymentSystem;
  paymentExternalId: string;
  value: number;
  confirmedDate: string;
}

export interface PaymentFilters {
  searchName?: string;
  systems?: string[];
  dates?: string[];
  customDateStart?: string;
  customDateEnd?: string;
}

interface UsePaymentsReturn {
  rows: PaymentRow[];
  filteredRows: PaymentRow[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

function parseLocalDate(iso: string): Date {
  const [year, month, day] = iso.split('-').map(Number);
  return new Date(year, month - 1, day);
}

export function usePayments(filters: PaymentFilters = {}): UsePaymentsReturn {
  const [rows, setRows] = useState<PaymentRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await requestEdgeFunction<{ data?: PaymentRow[] }>('payment-gateway', {
        method: 'POST',
        body: {
          action: 'list-portal-payments',
        },
      });

      setRows((response.data ?? []).map((row) => ({
        ...row,
        paymentSystem: row.paymentSystem as PaymentSystem,
      })));
    } catch (err: any) {
      console.error('[usePayments]', err);
      setError(err.message || 'Erro ao carregar pagamentos');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filteredRows = useMemo(() => rows
    .filter((row) => {
      const matchName = !filters.searchName
        || matchesSearchText(row.studentName, filters.searchName);
      const matchSystem = !filters.systems?.length
        || filters.systems.includes(row.paymentSystem);

      let matchDate = true;
      if (filters.dates?.length) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const paymentDate = parseLocalDate(row.confirmedDate.slice(0, 10));
        const diffDays = Math.round((today.getTime() - paymentDate.getTime()) / 86400000);

        matchDate = filters.dates.some((dateFilter) => {
          if (dateFilter === '7days') return diffDays >= 0 && diffDays <= 7;
          if (dateFilter === '30days') return diffDays >= 0 && diffDays <= 30;
          if (dateFilter === '90days') return diffDays >= 0 && diffDays <= 90;
          if (dateFilter !== 'custom') return false;

          if (!filters.customDateStart && !filters.customDateEnd) return true;
          const start = filters.customDateStart
            ? parseLocalDate(filters.customDateStart)
            : null;
          const end = filters.customDateEnd
            ? parseLocalDate(filters.customDateEnd)
            : null;
          if (start && paymentDate < start) return false;
          if (end) {
            const endOfDay = new Date(end);
            endOfDay.setHours(23, 59, 59, 999);
            if (paymentDate > endOfDay) return false;
          }
          return true;
        });
      }

      return matchName && matchSystem && matchDate;
    })
    .sort(
      (left, right) => (
        new Date(right.confirmedDate).getTime() - new Date(left.confirmedDate).getTime()
      ),
    ), [filters, rows]);

  return { rows, filteredRows, loading, error, refetch: fetchData };
}
